import { toast } from 'svelte-sonner';
import { toReqBody } from './save.svelte';
import type { SiteVisitMap, VehicleAssignmentMap } from './types';
import type { ValidationStoreInner } from './validate';

export type AutosaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export type AutosaveResult = {
    success: boolean;
    savedCount: number;
    skippedCount: number;
    error?: string;
};

/**
 * Creates a Set of IDs that should be excluded from autosave due to validation errors.
 */
export const getInvalidIds = (validationStore: ValidationStoreInner): Set<string> => {
    const invalidIds = new Set<string>();

    for (const job of validationStore.missingJobType) {
        invalidIds.add(job.id);
    }

    for (const job of validationStore.missingPurpose) {
        invalidIds.add(job.id);
    }

    return invalidIds;
};

/**
 * Clears modified flags for successfully saved items.
 */
export const clearModifiedFlags = (savedIds: string[], vehicleAssignments: VehicleAssignmentMap, siteVisits?: SiteVisitMap): void => {
    for (const id of savedIds) {
        const assignment = vehicleAssignments.get(id);
        if (assignment) {
            assignment.modified = false;
            continue;
        }

        if (siteVisits) {
            const visit = siteVisits.get(id);
            if (visit) {
                visit.modified = false;
            }
        }
    }
};

/**
 * Updates the map with new IDs from created assignments.
 * This handles the case where a new assignment was created on the backend.
 */
export const updateMapWithNewIds = (
    created: Array<{ temp_id: string; new_id: string; type: string }>,
    vehicleAssignments: VehicleAssignmentMap,
    siteVisits?: SiteVisitMap,
): void => {
    for (const { temp_id, new_id, type } of created) {
        if (type === 'vehicleAssignment') {
            const assignment = vehicleAssignments.get(temp_id);
            if (assignment) {
                // Update the id on the assignment object
                assignment.id = new_id;
                // Move from old key to new key in the map
                vehicleAssignments.delete(temp_id);
                vehicleAssignments.set(new_id, assignment);
            }
        } else if (type === 'siteVisit' && siteVisits) {
            const visit = siteVisits.get(temp_id);
            if (visit) {
                visit.id = new_id;
                siteVisits.delete(temp_id);
                siteVisits.set(new_id, visit);
            }
        }
    }
};

/**
 * Performs the autosave operation, filtering out invalid items.
 * Uses existing toReqBody function and filters the result.
 */
export const performAutosave = async ({
    baseUrl,
    csrfToken,
    vehicleAssignments,
    siteVisits,
    validationStore,
    onStatusChange,
}: {
    baseUrl: string;
    csrfToken: string;
    vehicleAssignments: VehicleAssignmentMap;
    siteVisits?: SiteVisitMap;
    validationStore: ValidationStoreInner;
    onStatusChange?: (status: AutosaveStatus) => void;
}): Promise<AutosaveResult> => {
    const invalidIds = getInvalidIds(validationStore);

    // Use existing toReqBody function to build request
    const fullRequestBody = toReqBody(vehicleAssignments, siteVisits);

    // Filter out invalid items (check both id and temp_id for new items)
    const filteredVehicleAssignments = fullRequestBody.vehicleAssignments.filter(
        (va) => !invalidIds.has(va.id ?? '') && !invalidIds.has(va.temp_id ?? ''),
    );
    const filteredSiteVisits = fullRequestBody.siteVisits.filter((sv) => !invalidIds.has(sv.id ?? ''));

    // Get IDs for items being saved (use temp_id for new items, id for existing)
    const savedIds = [...filteredVehicleAssignments.map((va) => va.temp_id ?? va.id), ...filteredSiteVisits.map((sv) => sv.id)].filter(
        (id): id is string => id !== undefined,
    );

    // Nothing valid to save
    if (filteredVehicleAssignments.length === 0 && filteredSiteVisits.length === 0) {
        return {
            success: true,
            savedCount: 0,
            skippedCount: invalidIds.size,
        };
    }

    onStatusChange?.('saving');

    try {
        const url = new URL('/web-api/schedule', baseUrl);
        const res = await fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                vehicleAssignments: filteredVehicleAssignments,
                siteVisits: filteredSiteVisits,
            }),
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json, text-plain, */*',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': csrfToken,
            },
            credentials: 'same-origin',
        });

        if (!res.ok) {
            const json = await res.json().catch(() => ({}));
            throw new Error(json?.message || `HTTP ${res.status}`);
        }

        const json = await res.json();

        // Update map with new IDs for created assignments
        if (json.created && Array.isArray(json.created)) {
            updateMapWithNewIds(json.created, vehicleAssignments, siteVisits);
        }

        // Clear modified flags for successfully saved items
        // Note: For new items, the map key has been updated, so we need to use the new IDs
        const finalIds = savedIds.map((id) => {
            const created = json.created?.find((c: { temp_id: string; new_id: string }) => c.temp_id === id);
            return created ? created.new_id : id;
        });
        clearModifiedFlags(finalIds, vehicleAssignments, siteVisits);

        onStatusChange?.('saved');

        const savedCount = filteredVehicleAssignments.length + filteredSiteVisits.length;
        toast.success(`Autosaved ${savedCount} item${savedCount !== 1 ? 's' : ''}`);

        return {
            success: true,
            savedCount,
            skippedCount: invalidIds.size,
        };
    } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'Unknown error';

        onStatusChange?.('error');

        // Error will be shown via error overlay in the component
        return {
            success: false,
            savedCount: 0,
            skippedCount: invalidIds.size,
            error: errorMessage,
        };
    }
};

/**
 * Creates a debounced autosave function.
 * Returns a cleanup function to cancel pending saves.
 */
export const createDebouncedAutosave = (
    delayMs: number = 5000,
): {
    trigger: (callback: () => Promise<void>) => void;
    cancel: () => void;
    isPending: () => boolean;
} => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const trigger = (callback: () => Promise<void>) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(async () => {
            timeoutId = null;
            await callback();
        }, delayMs);
    };

    const cancel = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };

    const isPending = () => timeoutId !== null;

    return { trigger, cancel, isPending };
};
